import { type FC, useState, useEffect, useCallback, useMemo } from "react";

import { AutoSizer, VirtualTable } from "@snapchat/snap-design-system";

import { SearchableCard } from "../../../common/components/SearchableCard";
import { useTableFilterAndSort } from "../../../common/hooks/useTableFilterAndSort";
import { useLoadGroupsV2 } from "../../containers/useLoadGroupsV2";
import { type GroupsGridItemModel } from "../../grid/model";
import { NoResults } from "../NoResults";

import { type GroupTableItem, getColumns } from "./AddToLensGroupTableColumns";

const emptySet = new Set();

type ExtendedGroupTableItem = GroupTableItem & Record<string, unknown>;

interface AddToLensGroupTableProps {
    selectedGroups: ReadonlyMap<string, GroupsGridItemModel>;
    onToggleSelection: (group: GroupsGridItemModel) => void;
    alreadySelectedIds?: Set<string>;
    newGroups: ReadonlySet<string>;
}

const AddToLensGroupTable: FC<AddToLensGroupTableProps> = ({
    onToggleSelection,
    selectedGroups,
    newGroups,
    alreadySelectedIds = emptySet,
}) => {
    const [searchText, setSearchText] = useState("");

    const { loading, loadMore, groups, isLastPage } = useLoadGroupsV2();
    const loadMoreData = useCallback(async () => loadMore(), [loadMore]);

    const columns = useMemo(() => getColumns({ onToggleSelection, newGroups }), [onToggleSelection, newGroups]);

    const onSearch = useCallback(
        (text: string) => {
            setSearchText(text);
        },
        [setSearchText],
    );

    const data = useMemo(
        () =>
            groups.map((group) => ({
                group,
                selected: selectedGroups.has(group.id as string),
                alreadySelected: alreadySelectedIds.has(group.id),
            })) as readonly GroupTableItem[],
        [groups, selectedGroups, alreadySelectedIds],
    );

    const getSearchKey = useCallback(({ group }: ExtendedGroupTableItem) => `${group.name} ${group.id}`, []);

    const { data: filteredData } = useTableFilterAndSort({
        tableData: data as ExtendedGroupTableItem[],
        searchText,
        getSearchKey,
    });

    useEffect(() => {
        if (!isLastPage && !loading) {
            loadMore();
        }
    }, [isLastPage, loading, loadMore]);

    return (
        <SearchableCard
            padBody={false}
            isLoading={!isLastPage}
            searchPlaceholder="Search Lens Group"
            onSearch={onSearch}
            searchValue={searchText}
            tooltip="Groups are still loading."
        >
            <AutoSizer disableHeight>
                {({ width }: { width: number }) => (
                    <VirtualTable
                        isLoading={loading && data.length === 0}
                        height={496}
                        width={width}
                        rowHeight={56}
                        columns={columns}
                        data={filteredData as ExtendedGroupTableItem[]}
                        loadMore={loadMoreData}
                        hasMore={!isLastPage}
                        emptyMessage={<NoResults description="No Groups found." />}
                    />
                )}
            </AutoSizer>
        </SearchableCard>
    );
};

export { AddToLensGroupTable };
